import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import type { LeadResponse, LeadStatusOption } from '@orion/contracts';
import { Button, Card, ConfirmDialog } from '@orion/ui';
import { ApiError } from '../../lib/api-client.js';
import { useToast } from '../../lib/toast.js';
import { useAuth } from '../../lib/auth.js';
import { PageHeader } from '../../app/shell.js';
import { Timeline } from '../activities/Timeline.js';
import { LogActivityComposer } from '../activities/LogActivityComposer.js';
import { NotesPanel } from '../notes/NotesPanel.js';
import { AttachmentsPanel } from '../attachments/AttachmentsPanel.js';
import { OpenTasksCard } from '../tasks/OpenTasksCard.js';
import { LeadMeetingsPanel } from '../calendar/LeadMeetingsPanel.js';
import { ConvertLeadModal } from './ConvertLeadModal.js';
import { useDeleteLead, useLeadQuery, useLeadStatusOptionsQuery, useLeadsQuery, useMergeLead, useUpdateLead } from './api.js';

type Tab = 'overview' | 'activity' | 'tasks' | 'notes' | 'attachments' | 'meetings';

/** docs/architecture screen "Lead — detail". design/orion-crm-design/
 * rendered-html/LeadDetail.html's right sidebar also shows a "No possible
 * duplicates" check and a "Related company" card — the former would mean
 * running the duplicate-check query on every page load for a card nobody
 * asked to see, so it's skipped. The latter — before conversion, a lead
 * usually only has a free-text `companyName`, no `Company` to link to — is
 * handled inline instead of as a separate card: `lead.company` is non-null
 * only when an automation's ingestion found-or-created one by name
 * (`IntegrationsService#upsertLead`), so the name below becomes a link to
 * it when that's happened, plain text otherwise. */
export function LeadDetailPage() {
  const { leadId } = useParams({ from: '/_app/leads/$leadId' });
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const { data: lead, isLoading, isError } = useLeadQuery(leadId);
  const { data: statuses } = useLeadStatusOptionsQuery();
  const updateLead = useUpdateLead(leadId ?? '');
  const deleteLead = useDeleteLead();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tab, setTab] = useState<Tab>('overview');
  const [resendPrefill, setResendPrefill] = useState<{ kind: 'email'; body: string } | null>(null);
  const resend = (bodyText: string) => {
    setTab('overview');
    setResendPrefill({ kind: 'email', body: bodyText });
  };
  // The `LeadConvert` design screen is this modal open over LeadDetail, not
  // a separate route — the design-diff harness can only navigate to a URL,
  // so `?__design=LeadConvert` (import.meta.env.DEV only) opens it directly.
  const [convertOpen, setConvertOpen] = useState(
    () => import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'LeadConvert',
  );

  if (isLoading) return <PageHeader title="Loading…" />;
  if (isError || !lead) {
    return (
      <>
        <PageHeader title="Lead not found" />
        <main className="p-7 text-sm text-text-muted">We couldn't find that lead, or you don't have access to it.</main>
      </>
    );
  }

  const isConverted = Boolean(lead.convertedAt);

  const setStatus = (statusId: string) => {
    if (statusId === lead.status.id) return;
    updateLead.mutate({ statusId, version: lead.version });
  };

  const runDelete = async () => {
    setConfirmDelete(false);
    try {
      await deleteLead.mutateAsync(lead.id);
      toast.success('Lead deleted.');
      navigate({ to: '/leads' });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to delete lead.');
    }
  };

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Leads', to: '/leads' }, { label: lead.fullName }]} />
      <main className="flex flex-1 flex-col gap-3.5 overflow-hidden px-7 pb-6 pt-5">
        <LeadHeaderCard
          lead={lead}
          statuses={statuses ?? []}
          onSetStatus={setStatus}
          onConvert={() => setConvertOpen(true)}
          isConverted={isConverted}
          canDelete={hasPermission('crm.leads.delete')}
          onDelete={() => setConfirmDelete(true)}
        />

        <nav aria-label="Lead sections" className="flex h-9.5 shrink-0 items-end gap-6 border-b border-border">
          {(
            [
              ['overview', 'Overview'],
              ['activity', 'Activity'],
              ['tasks', 'Tasks'],
              ['notes', 'Notes'],
              ['attachments', 'Attachments'],
              ['meetings', 'Meetings'],
            ] as const
          )
            .filter(([key]) => key !== 'meetings' || hasPermission('crm.meetings.view'))
            .map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={tab === key ? '-mb-px flex h-9.5 items-center border-b-2 border-primary text-sm font-semibold text-ink' : 'flex h-9.5 items-center text-sm font-medium text-text-muted hover:text-ink'}
            >
              {label}
            </button>
          ))}
        </nav>

        {tab === 'overview' && (
          <div className="grid min-h-0 flex-1 grid-cols-[280px_1fr_260px] gap-4">
            <AboutPanel lead={lead} />
            <div className="flex min-h-0 flex-col gap-3">
              <LogActivityComposer leadId={lead.id} phone={lead.phone} prefill={resendPrefill} onPrefillConsumed={() => setResendPrefill(null)} />
              <Card className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-4">
                <Timeline leadId={lead.id} onResend={resend} />
              </Card>
            </div>
            <div className="flex min-h-0 flex-col gap-2.5 overflow-y-auto">
              <OpenTasksCard leadId={lead.id} />
              {!isConverted && (
                <Card className="flex flex-col gap-2 border-dashed p-4">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">Related opportunity</span>
                  <div className="flex items-center gap-2.5">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2E47D6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="3" y="7" width="18" height="13" rx="2" />
                        <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                        <path d="M3 13h18" />
                      </svg>
                    </span>
                    <span className="flex flex-col">
                      <span className="text-[13px] font-semibold text-ink">No opportunity yet</span>
                      <button type="button" onClick={() => setConvertOpen(true)} className="text-left text-xs font-semibold text-primary-text hover:underline">
                        Convert to create an opportunity →
                      </button>
                    </span>
                  </div>
                </Card>
              )}
              {!isConverted && <MergeDuplicatePanel lead={lead} />}
            </div>
          </div>
        )}

        {tab === 'activity' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-4">
            <Timeline leadId={lead.id} onResend={resend} />
          </Card>
        )}
        {tab === 'tasks' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-4">
            <OpenTasksCard leadId={lead.id} bare />
          </Card>
        )}
        {tab === 'notes' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-4">
            <NotesPanel leadId={lead.id} />
          </Card>
        )}
        {tab === 'attachments' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-4">
            <AttachmentsPanel leadId={lead.id} />
          </Card>
        )}
        {tab === 'meetings' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-4">
            <LeadMeetingsPanel leadId={lead.id} />
          </Card>
        )}
      </main>
      {convertOpen && <ConvertLeadModal lead={lead} onClose={() => setConvertOpen(false)} />}
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this lead permanently?"
        description="This removes it from every list and search for good, along with its open and completed tasks. Notes and activity history stay on record, but there's no undo from here."
        confirmLabel="Delete"
        variant="danger"
        loading={deleteLead.isPending}
        onConfirm={runDelete}
        onClose={() => setConfirmDelete(false)}
        requireTypedConfirmation="DELETE"
      />
    </>
  );
}

function scoreRingColor(score: number): string {
  if (score >= 70) return '#1E7A4C';
  if (score >= 40) return '#C7781A';
  return '#B42318';
}

function LeadHeaderCard({
  lead,
  statuses,
  onSetStatus,
  onConvert,
  isConverted,
  canDelete,
  onDelete,
}: {
  lead: LeadResponse;
  statuses: LeadStatusOption[];
  onSetStatus: (id: string) => void;
  onConvert: () => void;
  isConverted: boolean;
  canDelete: boolean;
  onDelete: () => void;
}) {
  const initials = `${lead.firstName[0] ?? ''}${lead.lastName[0] ?? ''}`.toUpperCase();
  const funnelStatuses = statuses.filter((s) => ['new', 'contacted', 'qualified', 'converted'].includes(s.category));
  const currentIndex = funnelStatuses.findIndex((s) => s.id === lead.status.id);

  return (
    <Card className="flex shrink-0 flex-col">
      <div className="flex items-center gap-4 p-5">
        <span className="flex size-14 shrink-0 items-center justify-center rounded-full text-xl font-[650]" style={{ backgroundColor: `${lead.status.color}1A`, color: lead.status.color }}>
          {initials}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em] text-ink">{lead.fullName}</h1>
            <span className="flex h-6 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold" style={{ backgroundColor: `${lead.status.color}1A`, color: lead.status.color }}>
              <span className="size-1.5 rounded-full" style={{ backgroundColor: lead.status.color }} />
              {lead.status.name}
            </span>
            {lead.temperature === 'hot' && (
              <span className="flex h-5.5 items-center gap-1 rounded-[6px] bg-amber-soft px-2 text-xs font-semibold text-amber-text">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#C7781A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 3c1 3 4 5 4 9a4 4 0 0 1-8 0c0-2 1-3 1-3s1 2 2 2c0-3-1-5 1-8" />
                </svg>
                Hot
              </span>
            )}
            {lead.source?.name === 'Inbox Pulse (email reply)' && (
              <span className="flex h-5.5 items-center gap-1 rounded-[6px] px-2 text-xs font-semibold" style={{ backgroundColor: '#ECEFFD', color: '#2E47D6' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2E47D6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
                  <path d="m2.5 6.5 9.5 7 9.5-7" />
                </svg>
                via Inbox Pulse
              </span>
            )}
            {lead.priority === 'high' && (
              <span className="flex h-5.5 items-center gap-1 rounded-[6px] border border-border px-2 text-xs font-semibold text-danger">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="#B42318" aria-hidden="true">
                  <path d="M12 4 20 18H4z" />
                </svg>
                High priority
              </span>
            )}
            {lead.score !== null && (
              <span className="flex h-6 items-center gap-1.5 rounded-full bg-success-soft py-0 pl-1 pr-2.5 text-xs font-semibold text-success">
                <svg width="18" height="18" viewBox="0 0 40 40" aria-hidden="true">
                  <circle cx="20" cy="20" r="15" fill="none" stroke="#C4E4D2" strokeWidth="6" />
                  <circle
                    cx="20"
                    cy="20"
                    r="15"
                    fill="none"
                    stroke={scoreRingColor(lead.score)}
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={`${(lead.score / 100) * 94.2} 94.2`}
                    transform="rotate(-90 20 20)"
                  />
                </svg>
                Score {lead.score}
              </span>
            )}
          </div>
          <div className="text-sm leading-5 text-text-secondary">
            {lead.jobTitle ? `${lead.jobTitle} · ` : ''}
            {lead.company ? (
              <Link to="/companies/$companyId" params={{ companyId: lead.company.id }} className="font-semibold text-primary-soft-text hover:underline">
                {lead.company.name}
              </Link>
            ) : (
              <span className="font-semibold">{lead.companyName ?? 'No company'}</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-[13px] leading-5 text-text-muted">
            <span>Owner</span>
            {lead.owner ? (
              <span className="flex items-center gap-1.5 font-semibold text-ink">
                <span className="flex size-5 items-center justify-center rounded-full bg-avatar-indigo text-[9px] font-[650] text-white">
                  {lead.owner.displayName.slice(0, 2).toUpperCase()}
                </span>
                {lead.owner.displayName}
              </span>
            ) : (
              <span className="font-semibold text-amber-text">Unassigned</span>
            )}
            <span className="text-border-strong">·</span>
            <span>Created {new Date(lead.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canDelete && (
            <Button size="sm" variant="secondary" onClick={onDelete}>
              Delete
            </Button>
          )}
          {!isConverted && (
            <Button size="sm" onClick={onConvert}>
              Convert
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </Button>
          )}
        </div>
      </div>
      {funnelStatuses.length > 0 && (
        <div className="flex items-center gap-5 border-t border-divider px-5 py-2.5">
          <ol aria-label="Lead status" className="grid flex-1 grid-cols-4 gap-1">
            {funnelStatuses.map((s, i) => {
              const done = currentIndex >= 0 && i <= currentIndex;
              const current = s.id === lead.status.id;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => onSetStatus(s.id)}
                    aria-current={current ? 'step' : undefined}
                    className={`flex h-8 w-full items-center justify-center gap-1.5 text-[13px] font-semibold ${done ? 'bg-primary-soft text-primary-text' : 'bg-divider text-text-muted'} ${current ? 'bg-primary! text-white!' : ''}`}
                    style={{ clipPath: i === 0 ? 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)' : i === funnelStatuses.length - 1 ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 12px 50%)' : 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)' }}
                  >
                    {done && !current && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m5 12 5 5 9-10" />
                      </svg>
                    )}
                    {s.name}
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </Card>
  );
}

function AboutPanel({ lead }: { lead: LeadResponse }) {
  return (
    <Card className="flex flex-col gap-2.5 overflow-y-auto p-4">
      <h2 className="text-[15px] font-semibold text-ink">About</h2>
      <dl className="grid grid-cols-[88px_1fr] gap-x-2 gap-y-1.5 text-[13px] leading-4.5">
        <AboutRow label="Email" value={lead.email} href={lead.email ? `mailto:${lead.email}` : undefined} />
        <AboutRow label="Phone" value={lead.phone} />
        <AboutRow label="Website" value={lead.website} href={lead.website ?? undefined} />
        <AboutRow label="Address" value={lead.address?.formatted ?? null} />
        <AboutRow label="Source" value={lead.source?.name ?? null} />
        <AboutRow label="Created" value={new Date(lead.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
        <AboutRow label="Last contact" value={lead.lastContactedAt ? new Date(lead.lastContactedAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' }) : null} />
        <AboutRow label="Next follow-up" value={lead.nextFollowUpAt ? new Date(lead.nextFollowUpAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' }) : null} danger={Boolean(lead.nextFollowUpAt && new Date(lead.nextFollowUpAt) < new Date())} />
      </dl>
      {lead.tags.length > 0 && (
        <>
          <div className="h-px bg-divider" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">Tags</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {lead.tags.map((t) => (
              <span key={t.id} className="flex h-5.5 items-center rounded-[6px] border border-border px-2 text-xs font-medium text-text-secondary">
                {t.name}
              </span>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

function AboutRow({ label, value, href, danger }: { label: string; value: string | null | undefined; href?: string; danger?: boolean }) {
  return (
    <>
      <dt className="text-text-muted">{label}</dt>
      <dd className={`truncate ${danger ? 'font-semibold text-danger' : 'text-ink'}`}>
        {value ? (href ? <a href={href} className="hover:underline">{value}</a> : value) : <span className="text-text-faint">—</span>}
      </dd>
    </>
  );
}

/** docs/architecture/08-v1-scope.md § "Deduplication" — "lead merge" is a
 * Must-have alongside the create-time Use existing / Create anyway choice
 * (NewLeadPage): this is where an already-existing duplicate gets cleaned
 * up after the fact. Merging fills gaps on this lead from the one picked
 * here, re-links its tasks/notes/activity history, then soft-deletes it. */
function MergeDuplicatePanel({ lead }: { lead: LeadResponse }) {
  const [expanded, setExpanded] = useState(false);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data } = useLeadsQuery({ q: q || undefined, pageSize: 5 });
  const mergeLead = useMergeLead(lead.id);
  const candidates = (data?.data ?? []).filter((c) => c.id !== lead.id);

  const merge = async (duplicateId: string) => {
    setError(null);
    try {
      await mergeLead.mutateAsync(duplicateId);
      setQ('');
      setExpanded(false);
    } catch {
      setError('Could not merge that lead. Please try again.');
    }
  };

  if (!expanded) {
    return (
      <button type="button" onClick={() => setExpanded(true)} className="self-start text-xs font-semibold text-primary-text hover:underline">
        Merge a duplicate lead into this one
      </button>
    );
  }

  return (
    <Card className="p-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Merge a duplicate lead</h3>
      <p className="mb-3 text-sm text-text-muted">
        Search for the duplicate. Its tasks, notes and activity history move to {lead.fullName}, and any blank fields here get filled in.
      </p>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        type="search"
        placeholder="Search by name, company, email…"
        className="h-9 w-full rounded-control border border-border-strong bg-surface px-3 text-sm"
      />
      {error && (
        <div role="alert" className="mt-2 rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}
      {q && (
        <div className="mt-3 flex flex-col gap-2">
          {candidates.length === 0 && <p className="text-sm text-text-muted">No matching leads.</p>}
          {candidates.map((c) => (
            <div key={c.id} className="flex flex-col gap-1.5 rounded-control border border-border px-3 py-2 text-sm">
              <span className="truncate">
                {c.fullName} · {c.email ?? 'no email'}
              </span>
              <Button size="sm" variant="secondary" onClick={() => merge(c.id)} loading={mergeLead.isPending}>
                Merge into {lead.fullName}
              </Button>
            </div>
          ))}
        </div>
      )}
      <Button size="sm" variant="ghost" className="mt-3" onClick={() => setExpanded(false)}>
        Cancel
      </Button>
    </Card>
  );
}
